import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button, Space, Input, Select, InputNumber, Tabs, ColorPicker, message } from 'antd';
import {
    CloseOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    BgColorsOutlined,
    FontSizeOutlined,
    ColumnWidthOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * TokenEditorModal - Modal for creating and editing design tokens
 * Supports colors, typography, and spacing tokens
 */
const TokenEditorModal = ({
    visible,
    token, // null for create, object for edit
    category, // 'color', 'typography', 'spacing'
    canvasId,
    onClose,
    onSave,
    onDelete
}) => {
    const [loading, setLoading] = useState(false);
    const [editedToken, setEditedToken] = useState({});

    // Initialize from token or defaults
    useEffect(() => {
        if (token) {
            setEditedToken({ ...token });
        } else {
            // Default values based on category
            if (category === 'color') {
                setEditedToken({
                    name: '',
                    value: '#1677ff',
                    usage: ''
                });
            } else if (category === 'typography') {
                setEditedToken({
                    name: '',
                    fontFamily: 'Inter',
                    weight: 400,
                    sizes: { mobile: 16, tablet: 16, desktop: 16 }
                });
            } else if (category === 'spacing') {
                setEditedToken({
                    name: '',
                    values: { mobile: 8, tablet: 12, desktop: 16 }
                });
            }
        }
    }, [token, category]);

    const isEditing = !!token;

    const handleSave = async () => {
        if (!editedToken.name?.trim()) {
            message.error('Token name is required');
            return;
        }

        setLoading(true);
        try {
            await onSave?.(editedToken, isEditing);
            message.success(isEditing ? 'Token updated' : 'Token created');
            onClose();
        } catch (error) {
            message.error(error.message || 'Failed to save token');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!isEditing) return;
        setLoading(true);
        try {
            await onDelete?.(token.id);
            message.success('Token deleted');
            onClose();
        } catch (error) {
            message.error(error.message || 'Failed to delete token');
        } finally {
            setLoading(false);
        }
    };

    const getCategoryIcon = () => {
        switch (category) {
            case 'color': return <BgColorsOutlined style={{ color: '#58a6ff' }} />;
            case 'typography': return <FontSizeOutlined style={{ color: '#58a6ff' }} />;
            case 'spacing': return <ColumnWidthOutlined style={{ color: '#58a6ff' }} />;
            default: return null;
        }
    };

    const getCategoryLabel = () => {
        return category?.charAt(0).toUpperCase() + category?.slice(1);
    };

    // Render content based on category
    const renderColorEditor = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    TOKEN NAME
                </Text>
                <Input
                    value={editedToken.name}
                    onChange={(e) => setEditedToken(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., brand-primary"
                    style={{
                        background: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    COLOR VALUE
                </Text>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <ColorPicker
                        value={editedToken.value}
                        onChange={(color, hex) => setEditedToken(prev => ({ ...prev, value: hex }))}
                        showText
                        size="large"
                    />
                    <Input
                        value={editedToken.value}
                        onChange={(e) => setEditedToken(prev => ({ ...prev, value: e.target.value }))}
                        style={{
                            flex: 1,
                            background: '#161b22',
                            border: '1px solid #30363d',
                            borderRadius: 6
                        }}
                    />
                </div>
            </div>
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    USAGE DESCRIPTION
                </Text>
                <Input.TextArea
                    value={editedToken.usage}
                    onChange={(e) => setEditedToken(prev => ({ ...prev, usage: e.target.value }))}
                    placeholder="Describe when to use this color..."
                    rows={2}
                    style={{
                        background: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>
            {/* Preview */}
            <div style={{
                marginTop: 8,
                padding: 16,
                background: '#161b22',
                borderRadius: 8,
                border: '1px solid #30363d'
            }}>
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                    PREVIEW
                </Text>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: 8,
                        background: editedToken.value,
                        border: '2px solid #30363d',
                        boxShadow: `0 0 20px ${editedToken.value}40`
                    }} />
                    <div>
                        <Text style={{ color: '#e6edf3', fontWeight: 600, display: 'block' }}>
                            {editedToken.name || 'Unnamed'}
                        </Text>
                        <Text style={{ color: '#58a6ff', fontSize: 12 }}>
                            {editedToken.value}
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTypographyEditor = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    TOKEN NAME
                </Text>
                <Input
                    value={editedToken.name}
                    onChange={(e) => setEditedToken(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., heading-xl"
                    style={{
                        background: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 2 }}>
                    <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                        FONT FAMILY
                    </Text>
                    <Select
                        value={editedToken.fontFamily}
                        onChange={(value) => setEditedToken(prev => ({ ...prev, fontFamily: value }))}
                        style={{ width: '100%' }}
                        options={[
                            { value: 'Inter', label: 'Inter' },
                            { value: 'Roboto', label: 'Roboto' },
                            { value: 'Open Sans', label: 'Open Sans' },
                            { value: 'Poppins', label: 'Poppins' },
                            { value: 'SF Pro', label: 'SF Pro' },
                            { value: 'system-ui', label: 'System' }
                        ]}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                        WEIGHT
                    </Text>
                    <Select
                        value={editedToken.weight}
                        onChange={(value) => setEditedToken(prev => ({ ...prev, weight: value }))}
                        style={{ width: '100%' }}
                        options={[
                            { value: 300, label: 'Light (300)' },
                            { value: 400, label: 'Regular (400)' },
                            { value: 500, label: 'Medium (500)' },
                            { value: 600, label: 'Semibold (600)' },
                            { value: 700, label: 'Bold (700)' }
                        ]}
                    />
                </div>
            </div>
            {/* Responsive sizes */}
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    SIZES (px)
                </Text>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>
                            Mobile
                        </Text>
                        <InputNumber
                            value={editedToken.sizes?.mobile}
                            onChange={(value) => setEditedToken(prev => ({
                                ...prev,
                                sizes: { ...prev.sizes, mobile: value }
                            }))}
                            min={8}
                            max={120}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>
                            Tablet
                        </Text>
                        <InputNumber
                            value={editedToken.sizes?.tablet}
                            onChange={(value) => setEditedToken(prev => ({
                                ...prev,
                                sizes: { ...prev.sizes, tablet: value }
                            }))}
                            min={8}
                            max={120}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>
                            Desktop
                        </Text>
                        <InputNumber
                            value={editedToken.sizes?.desktop}
                            onChange={(value) => setEditedToken(prev => ({
                                ...prev,
                                sizes: { ...prev.sizes, desktop: value }
                            }))}
                            min={8}
                            max={120}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
            </div>
            {/* Preview */}
            <div style={{
                marginTop: 8,
                padding: 16,
                background: '#161b22',
                borderRadius: 8,
                border: '1px solid #30363d'
            }}>
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                    PREVIEW
                </Text>
                <Text style={{
                    color: '#e6edf3',
                    fontFamily: editedToken.fontFamily,
                    fontWeight: editedToken.weight,
                    fontSize: editedToken.sizes?.desktop || 16
                }}>
                    The quick brown fox
                </Text>
            </div>
        </div>
    );

    const renderSpacingEditor = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    TOKEN NAME
                </Text>
                <Input
                    value={editedToken.name}
                    onChange={(e) => setEditedToken(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., spacing-lg"
                    style={{
                        background: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>
            {/* Responsive values */}
            <div>
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                    VALUES (px)
                </Text>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>
                            Mobile
                        </Text>
                        <InputNumber
                            value={editedToken.values?.mobile}
                            onChange={(value) => setEditedToken(prev => ({
                                ...prev,
                                values: { ...prev.values, mobile: value }
                            }))}
                            min={0}
                            max={200}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>
                            Tablet
                        </Text>
                        <InputNumber
                            value={editedToken.values?.tablet}
                            onChange={(value) => setEditedToken(prev => ({
                                ...prev,
                                values: { ...prev.values, tablet: value }
                            }))}
                            min={0}
                            max={200}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>
                            Desktop
                        </Text>
                        <InputNumber
                            value={editedToken.values?.desktop}
                            onChange={(value) => setEditedToken(prev => ({
                                ...prev,
                                values: { ...prev.values, desktop: value }
                            }))}
                            min={0}
                            max={200}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
            </div>
            {/* Preview */}
            <div style={{
                marginTop: 8,
                padding: 16,
                background: '#161b22',
                borderRadius: 8,
                border: '1px solid #30363d'
            }}>
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                    PREVIEW (Desktop)
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: Math.min(editedToken.values?.desktop || 16, 64),
                        height: Math.min(editedToken.values?.desktop || 16, 64),
                        borderRadius: 4,
                        background: '#58a6ff',
                        transition: 'all 0.2s ease'
                    }} />
                    <div>
                        <Text style={{ color: '#e6edf3', fontWeight: 600, display: 'block' }}>
                            {editedToken.name || 'Unnamed'}
                        </Text>
                        <Text style={{ color: '#58a6ff', fontSize: 12 }}>
                            {editedToken.values?.desktop || 0}px
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderEditor = () => {
        switch (category) {
            case 'color': return renderColorEditor();
            case 'typography': return renderTypographyEditor();
            case 'spacing': return renderSpacingEditor();
            default: return null;
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={500}
            styles={{
                body: { padding: 0, background: '#0d1117' },
                content: { background: '#0d1117', borderRadius: 12 }
            }}
        >
            {/* Header */}
            <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #30363d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {getCategoryIcon()}
                    <Title level={5} style={{ margin: 0, color: '#e6edf3' }}>
                        {isEditing ? `Edit ${getCategoryLabel()} Token` : `New ${getCategoryLabel()} Token`}
                    </Title>
                </div>
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    style={{ color: '#8b949e' }}
                />
            </div>

            {/* Content */}
            <div style={{ padding: 20 }}>
                {renderEditor()}
            </div>

            {/* Footer */}
            <div style={{
                padding: '16px 20px',
                borderTop: '1px solid #30363d',
                display: 'flex',
                gap: 12,
                justifyContent: isEditing ? 'space-between' : 'flex-end'
            }}>
                {isEditing && (
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleDelete}
                        loading={loading}
                    >
                        Delete
                    </Button>
                )}
                <Space>
                    <Button onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        icon={isEditing ? <SaveOutlined /> : <PlusOutlined />}
                        onClick={handleSave}
                        loading={loading}
                    >
                        {isEditing ? 'Save Changes' : 'Create Token'}
                    </Button>
                </Space>
            </div>
        </Modal>
    );
};

export default TokenEditorModal;
